import { GeoPoint } from './useGeocode'
import { DEFAULT_PALLET_TYPE, PALLET_TYPES, PalletFamily, PalletTier } from '../../constants/pallets'
import { DEFAULT_SERVICE_LEVEL } from '../../constants/ratecards'

// 'pending' = auto-resolve in flight; 'resolved' = geoPoint set; 'not_found' = backend returned no
// candidates; 'error' = network/API failure during resolve. Optional for back-compat with older
// persisted data — manually-entered locations may not carry a status field.
export type ResolutionStatus = 'pending' | 'resolved' | 'not_found' | 'error'

export interface ZoneLocation {
  prefix: string
  city: string | null
  geoPoint: GeoPoint | null
  status?: ResolutionStatus
  // Plain-text reason a location is in `not_found` or `error` state. Surfaced in the editor row.
  resolveError?: string
}

export interface QuantityBreak {
  min: number
  max: number | null
  price: number
}

// Pallet caps. Lengths (height/width/depth) are in CENTIMETRES — the platform-canonical unit
// shared by the rate-card data model and the quote engine. Weight is in kilograms.
export interface PalletDimensions {
  height: number | null
  width: number | null
  depth: number | null
  weight: number | null
}

export interface ZoneServicePricing {
  serviceName: string
  unit: string
  breaks: QuantityBreak[]
  palletDimensions?: PalletDimensions
  palletType?: string
  unitValue?: number | null
}

// Network-wide surcharge (Saturday AM, Tail Lift, Customs Entry, Fuel Surcharge, etc.). Lives
// at the network root rather than per-zone because UK ratecards publish a single surcharges
// block that applies to the entire network. A row tagged with `selectableSurchargeType` is one of the
// delivery options a shipper can pick on a transport order, and the quote engine prices it off
// `price` + `unit`.
export interface Surcharge {
  id: string
  name: string
  price: number | null
  unit?: string
  notes?: string
  selectableSurchargeType?: string
}

export const FUEL_SURCHARGE_TYPE = 'Fuel surcharge'

export const SURCHARGE_TYPES = [
  { value: FUEL_SURCHARGE_TYPE, label: 'Fuel surcharge' },
  { value: 'Tail lift', label: 'Tail lift' },
  { value: 'AM delivery', label: 'AM delivery' },
  { value: 'PM delivery', label: 'PM delivery' },
  { value: 'Timed delivery', label: 'Timed delivery' },
  { value: 'SAT AM delivery', label: 'SAT AM delivery' },
] as const

export type SurchargeType = (typeof SURCHARGE_TYPES)[number]['value']

export const SURCHARGE_UNITS = [
  { value: 'per consignment', label: 'Per consignment' },
  { value: 'per pallet', label: 'Per pallet' },
] as const

export type SurchargeUnit = (typeof SURCHARGE_UNITS)[number]['value']

export const PERCENT_OF_CARRIAGE = '%'


export function isPricedSurcharge(surcharge: Surcharge): surcharge is Surcharge & { price: number } {
  return surcharge.price !== null
}

export function isFuelSurcharge(surcharge: Surcharge): boolean {
  return surcharge.selectableSurchargeType === FUEL_SURCHARGE_TYPE
}

export interface NetworkRateCardZone {
  id: string
  zoneName: string
  countryCode: string
  locations: ZoneLocation[]
  servicePricing: ZoneServicePricing[]
  // Source-format token list as the user / Copilot supplied it (e.g. "AL, CB1-5, PE38+"), kept
  // alongside the expanded `locations` so the editor can display "Imported as: …" without losing
  // the compact representation. Only set on Copilot-imported zones.
  originalPostcodesInput?: string
}

export const SHIPPING_UNITS = [
  { label: 'Pallet', value: 'Pallet' },
  { label: 'Kg', value: 'Kg' },
  { label: 'FTL', value: 'FTL' },
  { label: 'Hour', value: 'Hour' },
  { label: 'Mile', value: 'Mile' },
  { label: 'Day', value: 'Day' },
] as const

export const PALLET_UNIT = 'Pallet'

export {
  SERVICE_LEVELS,
  DEFAULT_SERVICE_LEVEL,
  MAXIMUM_FUEL_SURCHARGE_PERCENTAGE,
  isServiceLevel,
} from '../../constants/ratecards'
export type { ServiceLevel } from '../../constants/ratecards'

export type { PalletFamily, PalletTier } from '../../constants/pallets'

export interface NetworkPalletType {
  value: string
  shortLabel: string
  family: PalletFamily | null
  tier: PalletTier
  footprint: { lengthCm: number; widthCm: number } | null
  defaultCaps: { heightCm: number; weightKg: number } | null
}

const FAMILY_BOARD_FOOTPRINTS: Record<PalletFamily, { lengthCm: number; widthCm: number }> = {
  GB: { lengthCm: 120, widthCm: 100 },
  EU: { lengthCm: 120, widthCm: 80 },
}

export const NETWORK_PALLET_TYPES: NetworkPalletType[] = PALLET_TYPES.map((type) => ({
  value: type.value,
  shortLabel: type.shortLabel,
  family: type.family,
  tier: type.tier,
  footprint: type.family ? FAMILY_BOARD_FOOTPRINTS[type.family] : null,
  defaultCaps: type.tierCaps
    ? { heightCm: type.tierCaps.maxHeightCm, weightKg: type.tierCaps.maxWeightKg }
    : null,
}))

export { DEFAULT_PALLET_TYPE }

export function findPalletType(value: string | undefined | null): NetworkPalletType | undefined {
  return NETWORK_PALLET_TYPES.find((type) => type.value === value)
}

export function isKnownPalletType(value: string | undefined | null): boolean {
  return !!findPalletType(value)
}

export function isStandardPalletType(value: string | undefined | null): boolean {
  return !!findPalletType(value)?.footprint
}

export function getPalletTypeLabel(value: string | undefined | null): string {
  return findPalletType(value)?.shortLabel ?? value ?? 'no pallet type'
}

export function getDefaultPalletCaps(value: string | undefined | null): PalletDimensions | null {
  const type = findPalletType(value)
  if (!type?.footprint || !type.defaultCaps) return null
  return {
    height: type.defaultCaps.heightCm,
    width: type.footprint.widthCm,
    depth: type.footprint.lengthCm,
    weight: type.defaultCaps.weightKg,
  }
}

export function lockFootprint(palletType: string | undefined | null, dimensions: PalletDimensions): PalletDimensions {
  const footprint = findPalletType(palletType)?.footprint
  if (!footprint) return dimensions
  return { ...dimensions, width: footprint.widthCm, depth: footprint.lengthCm }
}

export interface ServiceIdentity {
  serviceName: string
  unit: string
  palletType?: string
}

export const DEFAULT_SERVICE: ServiceIdentity = {
  serviceName: DEFAULT_SERVICE_LEVEL,
  unit: PALLET_UNIT,
  palletType: DEFAULT_PALLET_TYPE,
}

export function toServiceIdentity(pricing: ServiceIdentity): ServiceIdentity {
  return {
    serviceName: pricing.serviceName,
    unit: pricing.unit,
    ...(pricing.palletType ? { palletType: pricing.palletType } : {}),
  }
}

export function getServiceKey(service: ServiceIdentity): string {
  return `${service.serviceName}|${service.unit}|${service.palletType ?? ''}`
}

export function isSameService(a: ServiceIdentity, b: ServiceIdentity): boolean {
  return getServiceKey(a) === getServiceKey(b)
}

export function getServiceLabel(service: ServiceIdentity): string {
  if (service.unit === PALLET_UNIT) return `${service.serviceName} · ${getPalletTypeLabel(service.palletType)}`
  return `${service.serviceName} (${service.unit})`
}

export function findService(zone: NetworkRateCardZone, service: ServiceIdentity): ZoneServicePricing | undefined {
  return zone.servicePricing.find((pricing) => isSameService(pricing, service))
}

export function removeServiceFromEveryZone(zones: NetworkRateCardZone[], service: ServiceIdentity): NetworkRateCardZone[] {
  return zones.map((zone) => ({
    ...zone,
    servicePricing: zone.servicePricing.filter((pricing) => !isSameService(pricing, service)),
  }))
}

export function getUniqueServices(zones: NetworkRateCardZone[]): ServiceIdentity[] {
  const seen = new Map<string, ServiceIdentity>()
  for (const zone of zones) {
    for (const pricing of zone.servicePricing) {
      if (!pricing.serviceName) continue
      const identity = toServiceIdentity(pricing)
      const key = getServiceKey(identity)
      if (!seen.has(key)) seen.set(key, identity)
    }
  }
  return Array.from(seen.values())
}

export function createServicePricing(service: ServiceIdentity): ZoneServicePricing {
  const caps = service.unit === PALLET_UNIT ? getDefaultPalletCaps(service.palletType) : null
  return {
    serviceName: service.serviceName,
    unit: service.unit,
    breaks: [{ min: 1, max: null, price: 0 }],
    ...(service.unit === PALLET_UNIT && service.palletType ? { palletType: service.palletType } : {}),
    ...(caps ? { palletDimensions: caps } : {}),
  }
}

// Currencies the platform's RatecardHelper.ValidateNetworkRatecard accepts. Mirror of the
// backend's Services.CommonModels.CurrencyCode constants — keep in sync if either side adds a
// code. Symbol is what the editor renders next to monetary inputs; code is what we ship to the
// backend.
export const NETWORK_RATECARD_CURRENCIES = [
  { code: 'GBP', symbol: '£', label: 'GBP (£)' },
  { code: 'USD', symbol: '$', label: 'USD ($)' },
  { code: 'EUR', symbol: '€', label: 'EUR (€)' },
  { code: 'SEK', symbol: 'kr', label: 'SEK (kr)' },
] as const

export const DEFAULT_NETWORK_RATECARD_CURRENCY = 'GBP'

export function getCurrencySymbol(code: string | undefined | null): string {
  if (!code) return '£'
  const match = NETWORK_RATECARD_CURRENCIES.find((c) => c.code === code.toUpperCase())
  // Unknown codes fall through to the code itself rather than guessing — the user sees the
  // letters they configured, which is better than silently defaulting to "£" and hiding a config
  // mismatch. Day-to-day this never fires because the backend allowlist matches the frontend.
  return match?.symbol ?? code
}

export function createEmptyZone(
  zoneName: string,
  services: ServiceIdentity[],
  countryCode: string
): NetworkRateCardZone {
  return {
    id: crypto.randomUUID(),
    zoneName,
    countryCode,
    locations: [],
    servicePricing: services.map(createServicePricing),
  }
}
